import type { Prompt, PromptVersion } from './prompt.types'

/**
 * Where a prompt's editable head stands relative to what is published.
 *
 * - `never-published` — no version exists, so nothing resolves this prompt yet
 * - `unpublished-changes` — the head differs from the newest version
 * - `published` — the head matches the newest version
 */
export type PromptDraftState = 'never-published' | 'unpublished-changes' | 'published'

/**
 * Compared by CONTENT rather than by `updatedAt` against `publishedAt`.
 *
 * A timestamp comparison reports a change for an edit that was saved and reverted,
 * and for one that touched only the name — neither of which changes what a render
 * produces. The question the banner asks is narrower: would publishing change what
 * callers get?
 *
 * So it compares the fields a version FREEZES. `body` and `variables` are the obvious
 * two, but a version also freezes `modelConfig` and `outputSchema` — changing the
 * temperature, swapping the model or adding an output shape all change what a caller
 * receives, and each of those used to leave the banner quiet, telling an author their
 * change was already live when it was not.
 *
 * `listSnapshot` is deliberately NOT compared. A version freezes the items of every
 * list it references, but those live on another row that this head does not carry, so
 * editing a shared list can still make a version stale without the banner knowing. The
 * head would have to resolve its lists to detect it, which is a fetch this pure
 * comparison cannot do.
 *
 * `versions` is expected newest-first, matching the API's `orderBy(desc(version))`.
 */
export function promptDraftState(prompt: Prompt, versions: PromptVersion[]): PromptDraftState {
	const newest = versions[0]
	if (!newest) return 'never-published'

	// Serialized rather than deep-compared: these are small, ordered, and round-trip
	// through JSON on both sides, so key order is stable.
	const differs = (a: unknown, b: unknown) => JSON.stringify(a) !== JSON.stringify(b)

	const changed =
		newest.body !== prompt.body ||
		differs(newest.variables, prompt.variables) ||
		// `null` and `undefined` both mean "no override" on the head, and a version
		// always carries a resolved config — so an absent head value is compared against
		// the version's rather than reported as a change.
		(prompt.modelConfig !== null &&
			prompt.modelConfig !== undefined &&
			differs(newest.modelConfig, prompt.modelConfig)) ||
		differs(newest.outputSchema ?? null, prompt.outputSchema ?? null)

	return changed ? 'unpublished-changes' : 'published'
}
